import { type ReactNode, useState } from 'react';
import { CaretDownIcon, PlusIcon, StorefrontIcon } from '@phosphor-icons/react';
import {
  Button,
  Popover,
  SearchField,
  cn,
  quietScrollbar,
} from '@skupreme/shared/ui';

import { useAuth } from '@contexts/auth';
import { useSidebar } from '@contexts/sidebar';
import { setOrdersScopeLevel } from '@features/account-view/scope';
import { useAccountSwitcher } from '@features/account-view/useAccountSwitcher';
import { useAccountView } from '@features/account-view/useAccountView';
import { useIsV2Account } from '@features/page-versions';
import { divisionLogoOf } from '@features/orders/lib/division-logo';
import { CommonUtility } from '@utility/common';
import UserService from '@utility/services/user';
import { ToastMessage } from '@utility/toast';

import { AccountGroupRows } from './AccountGroupRows';
import { AccountMark, switcherLabel } from './AccountMark';
import { ACCOUNT_LEVEL_LABEL, AccountLevelOption } from './AccountLevelOption';
import {
  countRows,
  getOrganizations,
  groupOrganizations,
  type Organization,
} from './organizations';

const wmsUrl = CommonUtility.isProductionEnv()
  ? 'https://warehouse.skupreme.com'
  : 'https://staging.warehouse.skupreme.com';

/* "Go to WMS" signs the user in over there with a one-time code: the code
   goes in the URL fragment, never the query string, so it stays out of
   referrers, server logs and analytics. The tab is opened first, while the
   click is still a user gesture, so popup blockers leave it alone; its
   opener is cut so the WMS page cannot reach back here. Anyone the handoff
   is not enabled for (403), or any failure, lands on the WMS login page,
   which is what the item always did. */
async function goToWms() {
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  const open = (url: string) => {
    if (tab) tab.location.href = url;
    else window.open(url, '_blank', 'noopener');
  };

  try {
    const { code } = await UserService.wmsHandoff();
    open(`${wmsUrl}/sso#code=${encodeURIComponent(code)}`);
  } catch {
    open(wmsUrl);
  }
}

/* The `menu` button is already a list row — full width, left aligned, no
   chrome. Only its fixed height has to give, because these rows carry the
   table's mark and are as tall as `Select` makes one: padded, not set. */
const LIST_ROW = cn(
  'sk:h-auto sk:shrink-0 sk:gap-2 sk:py-2',
  'sk:text-dense sk:font-normal sk:text-fg',
);

/* The plate the orders table frames a logo in. The button wears it at the
   table's own size, since it stands for the organization; `sm` is the square
   a nav row leaves for an icon, which is what a list row needs. */
function OrgMark({
  org,
  icon,
  size = 'md',
}: {
  org?: Organization;
  /** Stands in for the logo or initial, e.g. the account level's mark. */
  icon?: ReactNode;
  size?: 'sm' | 'md';
}) {
  const small = size === 'sm';

  return (
    <span
      className={cn(
        'sk:bg-surface sk:inset-shadow-hairline sk:flex sk:shrink-0 sk:items-center sk:justify-center sk:overflow-hidden',
        small ? 'sk:size-4.5 sk:rounded-sm' : 'sk:size-8 sk:rounded-lg',
      )}
    >
      {icon ? (
        icon
      ) : org?.logo?.url ? (
        <img
          src={org.logo.url}
          alt=""
          className={cn(
            'sk:object-contain',
            small ? 'sk:size-3.5' : 'sk:size-4.5',
          )}
        />
      ) : (
        <span
          className={cn(
            'sk:text-fg-secondary sk:font-semibold',
            small ? 'sk:text-[11px]' : 'sk:text-xs',
          )}
        >
          {org?.company_name?.[0]?.toUpperCase() || 'O'}
        </span>
      )}
    </span>
  );
}

/* One or two lines of text beside a mark: the second line only where a row
   has something to add under its name (the account's name). */
function OrgLabel({ label, secondary }: { label: string; secondary?: string }) {
  return (
    <span className="sk:text-fg sk:min-w-0 sk:flex-1 sk:truncate sk:text-left">
      {label}
      {secondary && (
        <span className="sk:text-fg-secondary sk:block sk:truncate sk:text-xs">
          {secondary}
        </span>
      )}
    </span>
  );
}

export type OrgRowProps = {
  org?: Organization;
  icon?: ReactNode;
  label: string;
  secondary?: string;
  selected: boolean;
  /** A division row, drawn under its account's row (room for the tree tick). */
  indent?: boolean;
  /** Without one the row is a plain header, not something to pick. */
  onPress?: () => void;
};

/** A row of the open list: the mark, the name, the fill of the chosen one. */
function OrgRow({
  org,
  icon,
  label,
  secondary,
  selected,
  indent,
  onPress,
}: OrgRowProps) {
  const content = (
    <>
      <OrgMark org={org} icon={icon} size="sm" />
      <OrgLabel label={label} secondary={secondary} />
    </>
  );
  if (!onPress) {
    return (
      <div className={cn(LIST_ROW, 'sk:flex sk:items-center sk:px-3')}>
        {content}
      </div>
    );
  }

  return (
    <Button
      variant="menu"
      /* The fill an open list marks its own choice with. */
      className={cn(
        LIST_ROW,
        indent && 'sk:pl-8',
        selected && 'sk:bg-fill-tertiary',
      )}
      onPress={onPress}
    >
      {content}
    </Button>
  );
}

/** Which organization the portal is showing, and the way to another. */
export function OrganizationSwitcher() {
  const {
    user,
    currentOrg,
    currentOrgInfo,
    isAdmin,
    switchOrganizationIfDiff,
  } = useAuth();

  // TODO: `SidebarContext` is created untyped, so every reader narrows it by
  // hand like this; type the context and these casts can all go.
  const { setShowWorkspace } = useSidebar() as {
    setShowWorkspace: (show: boolean) => void;
  };

  const { view, level } = useAccountView();
  const accounts = useAccountSwitcher();
  /* A new company is created on the legacy system only, with no V2 switches: not offered on V2. */
  const isV2Account = useIsV2Account();

  const [search, setSearch] = useState('');

  const legacyOrgs = getOrganizations(user, currentOrg, currentOrgInfo);
  const orgs = legacyOrgs.map(org => ({
    ...org,
    logo: {
      url:
        divisionLogoOf(
          { id: org._id, name: org.company_name },
          legacyOrgs,
          accounts,
        ) ?? undefined,
    },
  }));
  const groups = groupOrganizations(orgs, accounts, search, currentOrg);
  const unshown = search ? 0 : orgs.length - countRows(groups);

  /* The account the current division belongs to, for the closed label. At the
     account level the account view's own answer stands even when the list of
     accounts did not load. The label names one place: the account at its
     level, else the division. */
  const currentAccount = accounts.find(a =>
    a.divisionIds.includes(currentOrg ?? ''),
  );
  const atAccount = view.available && level === 'account';
  const accountName =
    currentAccount?.name ?? (view.available ? view.account.name : undefined);
  const divisionName = currentOrgInfo?.company_name || 'Select Company';
  const label = switcherLabel({
    atAccount,
    accountName,
    divisionName,
    accountLevelLabel: ACCOUNT_LEVEL_LABEL,
  });

  /* Make `id` the division in use at `level`. The level is stored first: the
     reload below starts the app on that division and reads it back. */
  const goTo = async (
    id: string,
    next: 'account' | 'division',
    close: () => void,
  ) => {
    close();
    setOrdersScopeLevel(id, next);
    if (id === currentOrg) return;

    try {
      await switchOrganizationIfDiff(id);
      /* Every screen and context below is still holding the organization that
         just left, so the switch is finished by loading the app again. */
      window.location.reload();
    } catch (error) {
      ToastMessage.apiError(error);
    }
  };

  /* In this account: the level changes where the user stands. In another:
     the first of its divisions the user holds, at the account level. A user
     who holds none of them stays where they are. */
  const pickAccount = (divisionIds: string[], close: () => void) => {
    const held = new Set(orgs.map(o => o._id));
    const target =
      currentOrg && divisionIds.includes(currentOrg)
        ? currentOrg
        : divisionIds.find(id => held.has(id));
    if (target) void goTo(target, 'account', close);
  };

  return (
    <Popover
      aria-label="Organization"
      className="sk:w-(--trigger-width) sk:min-w-64"
      trigger={
        <Button
          variant="menu"
          aria-label="Switch organization"
          /* Filled at rest so it reads as a control rather than another nav
             row; the hover step moves up with it. Inset like a row so the
             mark stands in the same column as the icons under it, and
             padded rather than fixed, because the mark is taller than the
             height a menu button would hold it to. */
          className="sk:bg-fill-tertiary sk:hover:bg-fill-secondary sk:h-auto sk:gap-2 sk:px-2 sk:py-1.5 sk:font-medium"
        >
          <OrgMark
            org={orgs.find(org => org._id === currentOrg) ?? currentOrgInfo}
            icon={
              atAccount ? (
                <AccountMark logoUrl={currentAccount?.logoUrl} size="md" />
              ) : undefined
            }
          />
          <OrgLabel label={label} />
          <CaretDownIcon size={14} />
        </Button>
      }
    >
      {({ close }) => (
        <div className="sk:flex sk:flex-col sk:gap-1">
          <div>
            <SearchField
              aria-label="Search organizations"
              placeholder="Search Company"
              value={search}
              onChange={setSearch}
              className="sk:w-full"
              autoFocus
            />
          </div>

          <div
            className={cn(
              'sk:flex sk:max-h-72 sk:flex-col sk:gap-1 sk:overflow-y-auto',
              quietScrollbar,
            )}
          >
            {view.available && !currentAccount && (
              <AccountLevelOption
                Row={OrgRow}
                accountName={view.account.name}
                selected={atAccount}
                onPress={() =>
                  pickAccount(currentOrg ? [currentOrg] : [], close)
                }
              />
            )}
            {groups.length === 0 ? (
              <span className="sk:text-fg-tertiary sk:px-2 sk:py-3 sk:text-center sk:text-dense">
                Nothing found
              </span>
            ) : (
              groups.map(group => (
                <AccountGroupRows
                  key={group.account?.id ?? group.divisions[0]._id}
                  group={group}
                  Row={OrgRow}
                  currentOrg={currentOrg}
                  accountSelected={
                    atAccount &&
                    (!group.account || group.account.id === currentAccount?.id)
                  }
                  onPickAccount={({ id }) =>
                    pickAccount(
                      accounts.find(a => a.id === id)?.divisionIds ?? [],
                      close,
                    )
                  }
                  onPickDivision={id => void goTo(id, 'division', close)}
                />
              ))
            )}
          </div>

          {unshown > 0 && (
            <span className="sk:text-fg-tertiary sk:px-3 sk:text-xs">
              +{unshown} more. Use search to find.
            </span>
          )}

          <div className="sk:border-rule sk:mt-1 sk:flex sk:flex-col sk:gap-1 sk:border-t sk:pt-1">
            {isAdmin && !isV2Account && (
              <Button
                variant="menu"
                className={LIST_ROW}
                onPress={() => {
                  close();
                  setShowWorkspace(true);
                }}
              >
                <PlusIcon size={18} />
                New Company
              </Button>
            )}
            <Button
              variant="menu"
              className={LIST_ROW}
              onPress={() => {
                close();
                void goToWms();
              }}
            >
              <StorefrontIcon size={18} />
              Go to WMS
            </Button>
          </div>
        </div>
      )}
    </Popover>
  );
}
